// @ts-nocheck
import { message } from 'antd';
import { useBlendAiService } from '@/features/ai-service/hooks/useBlendService';
import { useColorizeWindow } from '@/features/ai-service/hooks/useColorizeWindow';
import { useDeepBottomRail } from '@/features/ai-service/hooks/useDeepBottomRail';
import { useGrillBlend } from '@/features/ai-service/hooks/useGrillBlend';
import { useGetProductConfigurationJson } from '@/features/product-configurator/hooks/productConfiguration';
import { useCurrency } from '@/features/product-configurator/hooks/useCurrency';
import { Obj } from '@/types';
import { useQueryClient } from '@tanstack/react-query';
import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { BAY_POLES_DEFAULT, BOTH_SASHES_FIXED_VALUE, DIMENSION_OPTION, FOIL_SURCHARGE_RESOLVER, OPTION_TYPE_ANGLED_CILL_HORN, OPTION_TYPE_CILL, OPTION_TYPE_EGRESS_COMPLAINT, OPTION_TYPE_GLAZING_TYPE, OPTION_TYPE_HARDWARE_REQUIRED, OPTION_TYPE_HARDWARE_TYPE, OPTION_TYPE_INSIDE_SPRAY_COLOUR, OPTION_TYPE_OUTSIDE_SPRAY_COLOUR, OPTION_TYPE_SASH, OPTION_TYPE_SECURITY, OPTION_TYPE_SPRAY, OPTION_TYPE_TRANSOM_DROP, OPTION_TYPE_VARIATION, OPTION_TYPE_WINDOW_TYPE, ProductStructureType } from '../constants';
import { useGetBlendingResources, useGetDBRAnswer, useGetTemplateColors } from '../hooks';
import { useGrillAnswers } from '../hooks/useGrillAnswers';
import { IAssistantCart, IAssistantConfig, IAssistantMessage, IQuestion, IQuestionOption, IQuoteFormData, IWindowCartItem, WindowAnnotation } from '../types';
import { transformConfigurationPricing } from '../utils';
import { buildCouplingClipMessage, calculateBayPolesAndCouplingClips, COUPLING_CLIP_MSG_ID, DIMENSION_ALERT_ID, SPRAY_COLOUR_ALERT_ID } from '../utils/bayWindowCalculations';
import { calculatePriceFromMessages } from '../utils/configurationPriceCalculation';
import { filterQuestionOptions, getFormValuesFromMessages, getNextVisibleQuestion, getSkippedTrickleVentFrames, resolveVentWidth, shouldShowQuestion } from '../utils/displayCondition';
import { calculateFireEgress, calculateFireEgressMultiFrame, extractDimensionsFromMessages, FireEgressWindowType, OPTION_TYPE_CUSTOM_FRACTION, OPTION_TYPE_NUMERIC_VALUES, resolveSashHeights } from '../utils/fireEgress';
import { applyGlazingPriceToMessages } from '../utils/glazingPriceUpdate';
import {
    clearCustomerCart,
    loadCustomerCart,
    saveCustomerCart
} from '../utils/multiCustomerCartOperations';
import { evaluateOptionBasedRules, evaluateValueBasedRules, formatSurgeMessage } from '../utils/pricingCalculator';
import { getProductFromOption } from '../utils/product';
import { ARCH_FOIL_COLOUR_WIDTH_MIN } from '../utils/profileColour';
import { getFirstQuestion } from '../utils/question';
import { appQueryKey } from '@/utils/constant/appQueryKey';



interface AssistantState {
    messages: IAssistantMessage[];
    questionFlow: IQuestion[];
    egressConsentState: null | 'pending' | 'accepted' | 'rejected';
    cart: IAssistantCart;
    cartItems: IWindowCartItem[]; // For backward compatibility
    activeTab: string;
    selectedCustomerId: string | null;
    quoteData: IQuoteFormData | undefined;
    assistantConfig?: IAssistantConfig; // The active configuration with pricing applied
    blendedImageUrl?: string | null;
    onLineBlendImageUrl?: string | null;
    offlineImage?: string; // Base image for current window blending
    currentQuantity: number;
    isBlending: boolean;
    window?: WindowAnnotation;
    selectedProduct?: IQuestionOption;
}

interface AssistantMethods {
    // General message methods
    addMessage: (message: IAssistantMessage) => void;
    addBotMessage: (question: IQuestion, messageText?: string) => void;
    updateMessage: (index: number, updates: Partial<IAssistantMessage>) => void;
    clearMessages: () => void;
    addProcessingMessage: () => string; // Returns processing message ID
    removeProcessingMessage: (processingId: string) => void;

    // Single select methods
    handleSingleSelect: (questionId: string, option: IQuestionOption) => void;
    updateSingleSelect: (questionId: string, option: IQuestionOption) => void;

    // Multiple choice methods
    handleMultipleChoice: (questionId: string, options: IQuestionOption[]) => void;
    updateMultipleChoice: (questionId: string, options: IQuestionOption[]) => void;

    // Form methods
    handleFormSubmit: (questionId: string, formValues: Obj) => void;
    updateFormSubmit: (questionId: string, formValues: Obj) => void;

    // Text input methods
    handleTextInput: (questionId: string, textValue: string) => void;
    updateTextInput: (questionId: string, textValue: string) => void;

    // Cart methods
    addToCart: () => void;
    removeFromCart: (itemId: string) => void;
    updateQuantity: (itemId: string, quantity: number) => void;
    updateWindowName: (itemId: string, newName: string) => void;
    updateCartDiscount: (data: { discountPercentage?: number; discountAmount?: number; discountType: 'percentage' | 'amount' }) => void;
    duplicateCartItem: (
        itemId: string,
        customName?: string,
        overrideImages?: {
            propertyImageUrl?: string;
            blendedImageUrl?: string;
            onlineBlendedImageUrl?: string;
            windowAnnotationId?: string;
            selectedWindowBox?: { x: number; y: number; width: number; height: number; label?: string };
            /** All blended window boxes — populated when multiple windows were blended at once */
            selectedWindowBoxes?: Array<{ id: string; label: string; x: number; y: number; width: number; height: number }>;
        },
    ) => void;
    updateCartItem: (updatedItem: IWindowCartItem) => void;
    replaceCartConfiguration: (items: IWindowCartItem[]) => void;
    clearCart: () => void;

    // Quote flow methods
    setActiveTab: (tab: string) => void;
    setSelectedCustomerId: (customerId: string | null) => void;
    setQuoteData: (data: IQuoteFormData | undefined) => void;
    updateQuoteData: (updates: Partial<IQuoteFormData>) => void;

    // Current configuration methods
    setCurrentQuantity: (quantity: number) => void;

    // Blend mode methods
    setBlendMode: (mode: 'offline' | 'online') => void;

    // Fire egress consent methods
    acceptEgressConsent: () => void;
    rejectEgressConsent: () => void;
}

interface AssistantContextType {
    state: AssistantState;
    method: AssistantMethods;
}

const AssistantContext = createContext<AssistantContextType | undefined>(undefined);

interface AssistantProviderProps {
    children: ReactNode;
    /** Optional config override — when provided, skips the internal API fetch */
    initialConfig?: IAssistantConfig;
}

export const AssistantProvider: React.FC<AssistantProviderProps> = ({ children, initialConfig }) => {
    const [messages, setMessages] = useState<IAssistantMessage[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<IQuestionOption>();
    const [cart, setCart] = useState<IAssistantCart>();
    const [currentBlendMode, setCurrentBlendMode] = useState<'offline' | 'online'>('offline');
    // Quote flow states
    const [activeTab, setActiveTab] = useState('1');
    const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
    const [quoteData, setQuoteData] = useState<IQuoteFormData>();

    // Current configuration state
    const [currentQuantity, setCurrentQuantity] = useState(1);

    // Fire egress consent state
    const [egressConsentState, setEgressConsentState] = useState<null | 'pending' | 'accepted' | 'rejected'>(null);
    const [egressConsentAfterQuestionId, setEgressConsentAfterQuestionId] = useState<string | null>(null);

    // React Query client for cache invalidation
    const queryClient = useQueryClient();

    // Get configuration data — use initialConfig override if provided
    const { data: configDataRaw } = useGetProductConfigurationJson();
    const configData = initialConfig ?? (configDataRaw?.Config as any);
    const companyAccount: any = null;
    const { formatPrice } = useCurrency();

    const { color } = useGetTemplateColors({
        messages: messages
    })
    const { window, originalImage: offlineImage } = useGetBlendingResources({
        windows: quoteData?.customerInformation?.propertyImage?.windows ?? [],
        cart: cart,
        originalImage: quoteData?.customerInformation?.propertyImage?.imageUrl,
    })

    // Generate points from window for blending
    const points = React.useMemo(() => {
        if (!window) return [];
        return [{
            x: window.x,
            y: window.y,
            width: window.width,
            height: window.height,
        }];
    }, [window]);

    // Pipeline signature: which stages are currently needed. Computed directly
    // (not read back from dbrBlend/grillBlend's own outputs, to avoid a
    // circular dependency on the hooks we're about to call). Passed as
    // `forceKey` into ALL THREE blend hooks below — without this, each stage
    // only re-verifies when its OWN direct inputs change; when one stage
    // turns on/off (grill -> None at any level, DBR turned off, color
    // removed), the others have no reliable signal to re-run against the new
    // upstream state. This guarantees a genuine dependency change for every
    // stage whenever ANY stage's active/inactive status flips.
    const { isDbrEnabled: dbrEnabledPrecheck } = useGetDBRAnswer({ messages });
    const grillWindowsPrecheck = useGrillAnswers(messages);
    const grillNeedsToRunPrecheck = grillWindowsPrecheck.some((w) => w.topSash || w.bottomSash);
    const pipelineSignature = `dbr:${dbrEnabledPrecheck ? 1 : 0}|grill:${grillNeedsToRunPrecheck ? 1 : 0}|color:${color ? 1 : 0}`;

    // ── DBR (1st in pipeline) — deepens bottom rail on raw product thumbnail ──
    const dbrBlend = useDeepBottomRail(messages, selectedProduct?.thumbnail ?? null, selectedProduct, true, pipelineSignature);

    // ── Colorize (2nd in pipeline) — tints window frame with profile color hex, after DBR settles ──
    const colorizeInputUrl = dbrBlend.isApplying
        ? null
        : (dbrBlend.lastTaskStatus?.result_url ?? selectedProduct?.thumbnail ?? null);
    const colorizeBlend = useColorizeWindow({
        templateUrl: colorizeInputUrl,
        colorHex: color || null,
        enabled: true,
        selectedProduct: selectedProduct,
        forceKey: pipelineSignature,
    });
    const colorizedTemplateUrl = colorizeBlend.colorizedUrl ?? colorizeInputUrl ?? null;

    // ── Grill (3rd in pipeline) — only starts after DBR + colorize settle ──
    const grillWindowUrl = colorizeBlend.isApplying
        ? null
        : (colorizedTemplateUrl ?? null);
    const grillBlend = useGrillBlend(messages, grillWindowUrl, true, color || null, pipelineSignature, selectedProduct?.numberOfFrames ?? 1);
    // Grill is "settled" once it either has no answers to process, or has produced its result.
    // This avoids the race where grillBlend.isApplying is still false (debounce not yet fired)
    // right after colorize completes, which would fire the AI blend prematurely.
    const grillNeedsToRun = grillBlend.grillWindows.some((w: any) => w.topSash || w.bottomSash);
    const grillSettled = !grillNeedsToRun || (grillBlend.lastTaskStatus?.result_url !== null && !grillBlend.isApplying);

    // AI service only starts after DBR, colorize, and grill all fully settle
    const { blendedImageUrl, blendedStorageUrl, onLineBlendImageUrl, onlineBlendStorageUrl, isBlending, reset } = useBlendAiService({
        // When no grill is needed (Decorative Bar / grill answered "None" — at the
        // main/parent level, same-for-all, or individually per frame — useGrillAnswers
        // already normalizes all three to grillNeedsToRun=false), NEVER read
        // grillBlend.lastTaskStatus. useGrillBlend clears it asynchronously (its own
        // effect has to run and, in the all-none case, a code path that doesn't
        // update its "last applied" ref), so reading it here raced against that
        // clear and could feed a STALE grilled result_url into the final blend —
        // re-blending with the old grill still present instead of removing it.
        // Going straight to the DBR+colorize output removes that race entirely.
        selectedProductUrl: grillNeedsToRun
            ? (grillBlend.lastTaskStatus?.result_url || colorizedTemplateUrl || dbrBlend.lastTaskStatus?.result_url || selectedProduct?.thumbnail || '')
            : (colorizedTemplateUrl || dbrBlend.lastTaskStatus?.result_url || selectedProduct?.thumbnail || ''),
        enabled: !!selectedProduct && Boolean(selectedProduct?.optionId) && Boolean(offlineImage) && Boolean(window?.id)
            && !dbrBlend.isApplying
            && !colorizeBlend.isApplying
            && grillSettled,
        propertyImageUrl: offlineImage,
        windowLocations: points,
        onlineBlending: currentBlendMode === 'online',
        color: color ?? 'default',
    })

    // ─────────────────────────────────────────────────────────────────────────

    // Apply VSS discount and margin percentage to configuration prices.
    // Customer-level marginPercentage (from quote form) takes priority over company account default.
    const assistantConfig = React.useMemo(() => {
        if (!configData) return configData;

        const customerMargin = quoteData?.customerInformation?.marginPercentage;
        const marginPercentage = customerMargin ?? 0;
        const vssDiscountPercentage = companyAccount?.dogma_discountfromvsbaseprice || 0;

        return transformConfigurationPricing(configData, marginPercentage, vssDiscountPercentage);
    }, [configData, quoteData?.customerInformation?.marginPercentage, companyAccount?.dogma_discountfromvsbaseprice]);


    // Load cart from storage when customer ID changes
    useEffect(() => {
        const customerId = quoteData?.customerId || selectedCustomerId;
        if (customerId) {
            const savedCart = loadCustomerCart(customerId);
            if (savedCart) {
                setCart(savedCart);
            } else {
                setCart(undefined);
            }
            // Reset conversation state when customer changes
            setMessages([]);
            setSelectedProduct(undefined);
            setCurrentQuantity(1);
            isInitializingRef.current = false;
        }
    }, [selectedCustomerId, quoteData?.customerId]);
    React.useEffect(() => {
        if (messages.length > 0 && parseInt(activeTab) < 2) {
            setMessages([])
        }
    }, [activeTab])
    // Initialize chatbot with greeting and first question
    const isInitializingRef = useRef(false);
    useEffect(() => {
        // In preview mode (initialConfig provided) skip the tab check; otherwise require tab '2'
        if (!assistantConfig || messages.length > 0 || isInitializingRef.current) return;
        if (!initialConfig && activeTab !== '2') return;

        isInitializingRef.current = true;

        const greeting = assistantConfig.chatbotConfig?.greeting;
        const questionFlow = assistantConfig.questionFlow || [];

        // Add greeting message if available
        if (greeting) {
            const greetingMessage: IAssistantMessage = {
                type: 'assistant',
                message: greeting,
                question: {} as IQuestion,
                response: {
                    answer: [],
                    selectedOptionsIds: [],
                    form: {}
                },
                id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            };
            setMessages([greetingMessage]);
        }

        // Add processing message before first question
        const processingId = `processing-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        const processingMessage: IAssistantMessage = {
            type: 'assistant',
            message: '',
            question: {} as IQuestion,
            response: {
                answer: [],
                selectedOptionsIds: [],
                form: {}
            },
            id: processingId,
            isProcessing: true,
        };

        setTimeout(() => {
            setMessages(prev => [...prev, processingMessage]);
        }, greeting ? 500 : 100);

        // Start with first question after a delay
        setTimeout(() => {
            if (questionFlow.length > 0) {
                const firstQuestion = getFirstQuestion(questionFlow);
                if (firstQuestion) {
                    // Filter options based on display conditions
                    const filteredQuestion = filterQuestionOptions(firstQuestion, {}, undefined, {});

                    // Remove processing message and add first question
                    setMessages(prev => {
                        const withoutProcessing = prev.filter(msg => msg.id !== processingId);
                        const questionMessage: IAssistantMessage = {
                            type: 'assistant',
                            message: filteredQuestion.question,
                            question: filteredQuestion,
                            response: {
                                answer: [],
                                selectedOptionsIds: [],
                                form: {}
                            },
                            id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                        };
                        return [...withoutProcessing, questionMessage];
                    });
                }
            }
            isInitializingRef.current = false;
        }, greeting ? 1500 : 1000);
    }, [assistantConfig, messages.length, activeTab]);

    // Helper function to extract answers from messages grouped by category
    const getAnswersFromMessages = (msgs: IAssistantMessage[]): Record<string, IQuestionOption[]> => {
        const answersMap: Record<string, IQuestionOption[]> = {};

        msgs.forEach(msg => {
            if (msg.type === 'user' && msg.question?.category && msg.response?.answer) {
                const categoryId = msg.question.category;

                if (!answersMap[categoryId]) answersMap[categoryId] = [];
                answersMap[categoryId].push(...msg.response.answer);

                // Also index by frame (e.g. "catId.frame2") so per-frame dependency
                // checks use only that frame's answer and avoid cross-frame false positives.
                const frameMatch = msg.question.responsePath?.match(/frame(\d+)$/i);
                if (frameMatch?.[1]) {
                    const frameKey = `${categoryId}.frame${frameMatch[1]}`;
                    if (!answersMap[frameKey]) answersMap[frameKey] = [];
                    answersMap[frameKey].push(...msg.response.answer);
                }
            }
        });

        return answersMap;
    };

    // Helper function to evaluate pricing rules and add surge message
    const addPricingSurgeIfNeeded = (
        question: IQuestion,
        answers: IQuestionOption[] | Obj,
        updatedMessages: IAssistantMessage[]
    ): void => {
        // Get current answers as array of options first
        let currentAnswerOptions: IQuestionOption[] = [];
        if (Array.isArray(answers)) {
            currentAnswerOptions = answers;
        } else {
            // Convert form/text values to options
            Object.entries(answers).forEach(([key, value]) => {
                const option = question.options?.find(opt =>
                    opt.responseValue === key || opt.optionId === key
                );
                if (option) {
                    currentAnswerOptions.push(option);
                }
            });
        }

        // Collect pricing rules from both question level AND selected options
        const pricingRules: any[] = [];

        // 1. Add question-level pricing rules
        if (question.priceConditions && question.priceConditions.length > 0) {
            pricingRules.push(...question.priceConditions);
        }

        // 2. Add option-level pricing rules from selected options
        currentAnswerOptions.forEach(option => {
            if (option.priceConditions && option.priceConditions.length > 0) {
                pricingRules.push(...option.priceConditions);
            }
        });

        if (pricingRules.length === 0) return;

        const allSurgeItems: any[] = [];

        // Extract all answers from messages for option-based rules
        const allAnswers = getAnswersFromMessages(updatedMessages);


        const marginPercentage = quoteData?.customerInformation?.marginPercentage ?? 0;

        // 1. Evaluate value-based rules (numeric conditions)
        if (Array.isArray(answers)) {
            // Array of options (single/multiple select)
            answers.forEach(option => {
                const surgeItems = evaluateValueBasedRules(
                    question,
                    option.responseValue || option.optionText,
                    option,
                    pricingRules,
                    allAnswers, // Pass all answers for dimension calculation
                    assistantConfig?.questionFlow ?? [], // Pass questions for reference
                    updatedMessages, // Pass messages for form data extraction
                    formatPrice,
                    marginPercentage
                );
                allSurgeItems.push(...surgeItems);
            });
        } else {
            // Form values object (text input or form)
            // Coupled/Bay: individual frame widths are present → skip overall_width
            // so each frame is checked independently (not the combined total).
            const hasFrameWidths = Object.keys(answers).some(k => /^window_\d+_width_/i.test(k));

            Object.entries(answers).forEach(([key, value]) => {
                if (key.startsWith('__')) return;
                if (hasFrameWidths && /^(overall_width|width)_\(mm\)$/.test(key)) return;

                // Fall back to questionFlow when question.options is slimmed from storage
                const option = question.options?.find(opt =>
                    opt.responseValue === key || opt.optionId === key
                ) ?? (assistantConfig?.questionFlow ?? [])
                    .find((q: any) => q.questionId === question.questionId)
                    ?.options?.find((opt: any) => opt.responseValue === key || opt.optionId === key);

                if (option || key === 'textValue') {
                    const surgeItems = evaluateValueBasedRules(
                        question,
                        value as string | number,
                        option,
                        pricingRules,
                        allAnswers,
                        assistantConfig?.questionFlow ?? [],
                        updatedMessages,
                        formatPrice,
                        marginPercentage
                    );
                    allSurgeItems.push(...surgeItems);
                }
            });
        }

        // 2. Evaluate option-based rules (combination of selected options)
        const optionBasedSurgeItems = evaluateOptionBasedRules(
            currentAnswerOptions,
            allAnswers,
            pricingRules,
            assistantConfig?.questionFlow ?? [],
            updatedMessages, // Pass messages for form data extraction
            formatPrice,
            marginPercentage
        );
        allSurgeItems.push(...optionBasedSurgeItems);

        // Remove duplicate surge items — same rule on the same option is a duplicate,
        // but the same rule on different frame options (e.g. window_1 vs window_2) is not.
        let uniqueSurgeItems = allSurgeItems.filter((item, index, self) =>
            index === self.findIndex(t =>
                t.ruleId === item.ruleId &&
                (t.triggerOption?.optionId || '') === (item.triggerOption?.optionId || '')
            )
        );

        // If the foil surcharge already exists in any saved cart window, suppress it for
        // the window currently being configured so it is never charged twice per quote.
        const foilAlreadyInCart = (cart?.windowConfiguration ?? []).some(w =>
            w.messages.some(m =>
                m.type === 'price-surge' &&
                (m.surgeItems ?? []).some((s: any) => s.resolverValue === FOIL_SURCHARGE_RESOLVER)
            )
        );
        if (foilAlreadyInCart) {
            uniqueSurgeItems = uniqueSurgeItems.filter(s => s.resolverValue !== FOIL_SURCHARGE_RESOLVER);
        }

        // If pricing rules are triggered, add price surge message
        if (uniqueSurgeItems.length > 0) {
            // Build response object with current answers
            let responseData: {
                answer: IQuestionOption[];
                selectedOptionsIds: string[];
                form: Obj;
            };

            if (Array.isArray(answers)) {
                // Array of options
                responseData = {
                    answer: answers,
                    selectedOptionsIds: answers.map(opt => opt.optionId),
                    form: {}
                };
            } else {
                // Form values object
                const formAsOptions: IQuestionOption[] = Object.entries(answers).map(([key, value]) => {
                    const option = question.options?.find(opt =>
                        opt.responseValue === key || opt.optionId === key
                    );
                    return option || {
                        optionId: key,
                        vssPrice: 0,
                        optionText: String(value),
                        responseValue: String(value),
                        description: '',
                        numberOfFrames: 0,
                        optionType: 0,
                        inputType: 0,
                        optionTypeName: '',
                        order: 0,
                        price: 0,
                        priceModifier: 0,
                        thumbnail: ''
                    };
                });

                responseData = {
                    answer: formAsOptions,
                    selectedOptionsIds: formAsOptions.map(opt => opt.optionId),
                    form: answers
                };
            }

            // Remove existing surge messages that contain any of the surge items we're about to add
            // (identified by resolverValue) to prevent duplicates when answering spray colours
            const newResolverValues = new Set(uniqueSurgeItems.map(s => s.resolverValue).filter(Boolean));
            const filtered = updatedMessages.filter(m => {
                if (m.type !== 'price-surge') return true;
                const existingResolvers = (m.surgeItems ?? []).map((s: any) => s.resolverValue);
                return !existingResolvers.some(r => newResolverValues.has(r));
            });
            updatedMessages.splice(0, updatedMessages.length, ...filtered);

            const surgeBubbleMessage: IAssistantMessage = {
                id: `surge_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
                type: 'price-surge',
                message: formatSurgeMessage(uniqueSurgeItems, formatPrice),
                question: question, // Include the question that triggered the surge
                response: responseData, // Include the answer that triggered the surge
                surgeItems: uniqueSurgeItems
            };
            updatedMessages.push(surgeBubbleMessage);
        }
    };

    // Proceed to next question after user response
    const proceedToNextQuestion = (
        questionId: string,
        processingId: string,
        currentMessages: IAssistantMessage[],
        currentSelectedProduct?: IQuestionOption
    ) => {
        setTimeout(() => {
            // Extract answers from provided messages
            const currentAnswers = getAnswersFromMessages(currentMessages);
            const qFlow = assistantConfig?.questionFlow ?? [];

            // Get next visible question based on display conditions
            const nextQuestion = getNextVisibleQuestion(
                questionId,
                qFlow,
                currentAnswers,
                currentSelectedProduct,
                false,
                getFormValuesFromMessages(currentMessages)
            );

            // Remove processing message
            removeProcessingMessage(processingId);

            // Compute question indices once for both hardware-skip and trickle-vent-skip checks.
            const qIds = qFlow.map((q: any) => q.questionId);
            const curIdx = qIds.indexOf(questionId);
            const nxtIdx = nextQuestion ? qIds.indexOf(nextQuestion.questionId) : qFlow.length;
            const currentFV = getFormValuesFromMessages(currentMessages);

            // Inject hardware skip alert at the exact position where hardware would have rendered.
            // Guard: never fire when user explicitly said Yes to Hardware Required.
            const hardwareRequiredYes = Object.values(currentAnswers).some(opts =>
                opts.some(o => o.optionType === OPTION_TYPE_HARDWARE_REQUIRED && o.responseValue === 'yes')
            );
            // Hardware type (61) is now per-frame — only alert when NO hardware type question is visible.
            const hardwareQs: any[] = qFlow.filter((q: any) => q.optionType === OPTION_TYPE_HARDWARE_TYPE);
            const hardwareQ = hardwareQs[0];
            const anyHardwareTypeVisible = hardwareQs.some(
                (q: any) => shouldShowQuestion(q, currentAnswers, currentSelectedProduct, false, currentFV)
            );
            if (!hardwareRequiredYes && hardwareQ && !anyHardwareTypeVisible && hardwareQs.length > 0) {
                const hwIdx = qIds.indexOf(hardwareQ.questionId);
                const hardwarePassedHere = hwIdx >= 0 &&
                    (curIdx < 0 || hwIdx > curIdx) &&
                    hwIdx < nxtIdx;
                if (hardwarePassedHere) {
                    const hardwareRequiredNo = Object.values(currentAnswers).some(opts =>
                        opts.some(o => o.optionType === OPTION_TYPE_HARDWARE_REQUIRED && o.responseValue === 'no')
                    );
                    const bothSashesFixed = Object.values(currentAnswers).some(opts =>
                        opts.some(o => o.optionType === OPTION_TYPE_SASH && o.responseValue === BOTH_SASHES_FIXED_VALUE)
                    );
                    const skipMessage = hardwareRequiredNo
                        ? 'You selected No for Hardware Required. As a result, all hardware questions have been skipped.'
                        : bothSashesFixed
                            ? 'You have selected Both Sashes Fixed for all frames. As a result, no hardware is required and the hardware selection has been skipped.'
                            : 'Hardware is not applicable for your current configuration and has been skipped.';
                    setMessages(prev => {
                        if (prev.some(m => m.id?.startsWith('hardware_skip_alert_'))) return prev;
                        return [...prev, {
                            id: `hardware_skip_alert_${Date.now()}`,
                            type: 'alert' as const,
                            alertVariant: 'info' as const,
                            alertTitle: 'No Hardware Required',
                            message: skipMessage,
                            question: {} as any,
                            response: { answer: [], selectedOptionsIds: [], form: {} },
                        }];
                    });
                }
            }

            // Trickle-vent skip alerts — inject only at the exact position where
            // each question would have appeared, not at the dimension form position.
            const skippedTrickle = getSkippedTrickleVentFrames(qFlow, currentFV, currentAnswers, currentSelectedProduct);
            for (const info of skippedTrickle) {
                const trickleIdx = qIds.indexOf(info.questionId);
                const passedHere = trickleIdx >= 0 &&
                    (curIdx < 0 || trickleIdx > curIdx) &&
                    trickleIdx < nxtIdx;
                if (!passedHere) continue;
                const alertId = `trickle_vent_skip_alert_${info.questionId}`;
                const reason = info.widthMin !== undefined && info.frameWidth < info.widthMin
                    ? `The frame width (${info.frameWidth}mm) is below the minimum required width of ${info.widthMin}mm for trickle vent installation.`
                    : `The frame width (${info.frameWidth}mm) exceeds the maximum allowed width of ${info.widthMax}mm for trickle vent installation.`;
                setMessages(prev => {
                    if (prev.some(m => m.id === alertId)) return prev;
                    return [...prev, {
                        id: alertId,
                        type: 'alert' as const,
                        alertVariant: 'info' as const,
                        alertTitle: `Trickle Vents Not Available — ${info.frameLabel}`,
                        message: `Trickle vents have been skipped for ${info.frameLabel}. ${reason}`,
                        question: {} as any,
                        response: { answer: [], selectedOptionsIds: [], form: {} },
                    }];
                });
            }

            // Spray skip alert — inject once when the flow passes the Spray question
            // position on an arch window that is too narrow for Foil (< 800mm).
            const sprayQ = qFlow.find((q: any) => q.optionType === OPTION_TYPE_SPRAY);
            if (sprayQ && currentSelectedProduct?.producttypecode === ProductStructureType.Arch) {
                const sprayIdx = qIds.indexOf(sprayQ.questionId);
                const sprayPassedHere = sprayIdx >= 0 &&
                    (curIdx < 0 || sprayIdx > curIdx) &&
                    sprayIdx < nxtIdx;
                if (sprayPassedHere) {
                    const width = resolveVentWidth(currentFV);
                    if (width > 0 && width < ARCH_FOIL_COLOUR_WIDTH_MIN) {
                        const alertId = `spray_skip_alert_arch`;
                        setMessages(prev => {
                            if (prev.some(m => m.id === alertId)) return prev;
                            return [...prev, {
                                id: alertId,
                                type: 'alert' as const,
                                alertVariant: 'info' as const,
                                alertTitle: 'Spray Colour Not Available',
                                message: `Spray colour is only available for arch windows with Foil profile colour (minimum ${ARCH_FOIL_COLOUR_WIDTH_MIN}mm wide). As this window is ${width}mm wide with Standard White profile, spray colour has been skipped.`,
                                question: {} as any,
                                response: { answer: [], selectedOptionsIds: [], form: {} },
                            }];
                        });
                    }
                }
            }

            // Show next question if available
            if (nextQuestion) {
                // Before showing the "Hardware Required" question (fixed-sash opt-in),
                // inject an info alert so the user understands the context.
                if (nextQuestion.optionType === OPTION_TYPE_HARDWARE_REQUIRED) {
                    setMessages(prev => {
                        if (prev.some(m => m.id === 'hardware_required_alert')) return prev;
                        return [...prev, {
                            id: 'hardware_required_alert',
                            type: 'alert' as const,
                            alertVariant: 'info' as const,
                            alertTitle: 'Hardware on Fixed Sash',
                            message: 'This window has a fixed sash. Hardware is not normally required for fixed sash windows, but you can choose to add it if needed.',
                            question: {} as any,
                            response: { answer: [], selectedOptionsIds: [], form: {} },
                        }];
                    });
                }

                // Filter options based on display conditions
                const filteredQuestion = filterQuestionOptions(
                    nextQuestion,
                    currentAnswers,
                    currentSelectedProduct,
                    getFormValuesFromMessages(currentMessages)
                );

                addBotMessage(filteredQuestion);
            } else {
                // No more questions - conversation complete
            }
        }, 500);
    };

    // Helper to extract selected product from messages
    const getSelectedProductFromMessages = (msgs: IAssistantMessage[]): IQuestionOption | undefined => {
        // Find the most recent product selection (VARIATION or WINDOW_TYPE)
        for (let i = msgs.length - 1; i >= 0; i--) {
            const msg = msgs[i];
            if (!msg) continue;
            if (msg.type === 'user' && msg.response?.answer) {
                for (const option of msg.response.answer) {
                    if ([OPTION_TYPE_VARIATION, OPTION_TYPE_WINDOW_TYPE].includes(option.optionType)) {
                        const product = getProductFromOption(option);
                        if (product) {
                            return product;
                        }
                    }
                }
            }
        }
        return undefined;
    };

    // Reset subsequent messages and proceed to next question
    const resetAndProceedToNext = (
        questionId: string,
        updatedMessages: IAssistantMessage[],
        option?: IQuestionOption
    ) => {
        // Clear any previous egress consent state so re-check starts fresh
        setEgressConsentState(null);
        setEgressConsentAfterQuestionId(null);

        // Determine the selected product to use
        let productToUse: IQuestionOption | undefined;

        if (option && [OPTION_TYPE_VARIATION, OPTION_TYPE_WINDOW_TYPE].includes(option.optionType)) {
            const product = getProductFromOption(option);
            productToUse = product;
        } else {
            productToUse = getSelectedProductFromMessages(updatedMessages);
        }

        setSelectedProduct(productToUse);

        // Fire egress: re-check when the Egress Complaint question is re-answered as Yes
        const egressMsg = updatedMessages.find(m => m.question?.questionId === questionId);
        if (
            egressMsg?.question?.optionType === OPTION_TYPE_EGRESS_COMPLAINT &&
            egressMsg.response?.answer?.[0]?.responseValue === 'yes'
        ) {
            const egressResult = checkFireEgress(updatedMessages);
            const alertMsg = buildEgressAlertMessage(egressResult);
            const msgsToSet = alertMsg ? [...updatedMessages, alertMsg] : updatedMessages;
            setMessages(msgsToSet);
            if (egressResult?.fails) {
                setEgressConsentState('pending');
                setEgressConsentAfterQuestionId(questionId);
                return;
            }
            const processingId = addProcessingMessage();
            setTimeout(() => {
                proceedToNextQuestion(questionId, processingId, msgsToSet, productToUse);
            }, 100);
            return;
        }

        const strippedMsgs = applyHardwareSkipAlert(questionId, updatedMessages);
        setMessages(strippedMsgs);
        const processingId = addProcessingMessage();
        setTimeout(() => {
            proceedToNextQuestion(questionId, processingId, strippedMsgs, productToUse);
        }, 100);
    };

    // General message methods
    const addMessage = (message: IAssistantMessage) => {
        const messageWithId = {
            ...message,
            id: message.id || `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };
        setMessages(prev => [...prev, messageWithId]);
    };

    const addBotMessage = (question: IQuestion, messageText?: string) => {
        // Check if this question already exists in messages (prevent duplicates)
        setMessages(prev => {
            const exists = prev.some(
                msg => msg.type === 'assistant' &&
                    msg.question?.questionId === question.questionId &&
                    !msg.isProcessing
            );

            if (exists) {

                return prev;
            }

            const botMessage: IAssistantMessage = {
                id: `bot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                type: 'assistant',
                message: messageText || question.question,
                question: question,
                response: {
                    answer: [],
                    selectedOptionsIds: [],
                    form: {}
                }
            };

            return [...prev, botMessage];
        });

    };

    const updateMessage = (index: number, updates: Partial<IAssistantMessage>) => {
        setMessages(prev => {
            const updated = [...prev];
            if (index >= 0 && index < updated.length) {
                updated[index] = { ...updated[index], ...updates };
            }
            return updated;
        });
    };

    const clearMessages = () => {
        setMessages([]);
        setEgressConsentState(null);
        setEgressConsentAfterQuestionId(null);
    };

    // Add a processing message and return its ID
    const addProcessingMessage = (): string => {
        const processingId = `processing_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const processingMessage: IAssistantMessage = {
            id: processingId,
            type: 'assistant',
            message: '',
            question: {} as IQuestion,
            response: {
                answer: [],
                selectedOptionsIds: [],
                form: {}
            },
            isProcessing: true
        };
        setMessages(prev => [...prev, processingMessage]);
        return processingId;
    };

    // Remove a processing message by ID
    const removeProcessingMessage = (processingId: string) => {
        setMessages(prev => prev.filter(msg => msg.id !== processingId));
    };

    // Helper to create user response message
    const createUserResponseMessage = (
        question: IQuestion,
        answer: IQuestionOption[],
        form?: Obj
    ): IAssistantMessage => {
        const selectedIds = answer.map(opt => opt.optionId);

        // Format message based on whether it's a form submission or option selection
        let message = '';
        if (form && Object.keys(form).length > 0) {
            // Format form values as "Field: Value". Skip metadata keys (e.g.
            // __split_mode__) which exist only for UI-state restoration.
            message = Object.entries(form)
                .filter(([key]) => !key.startsWith('__'))
                .map(([key, value]) => {
                    // Find the option to get the display name
                    const option = answer.find(opt => opt.optionId === key || opt.responseValue === key);
                    const label = option?.optionText || key;
                    return `${label}: ${value}`;
                })
                .join(', ');
        } else {
            // For regular options, just show the option text
            message = answer.map(opt => opt.optionText).join(', ');
        }

        return {
            id: `user_${Date.now()}_${Math.random().toString(36)}`,
            type: 'user',
            message: message,
            question: question,
            response: {
                answer: answer,
                selectedOptionsIds: selectedIds,
                form: form || {}
            }
        };
    };

    // ─── Fire egress consent helpers ─────────────────────────────────────────

    const TRANSOM_FRACTION_RESP = 'custom_fraction_from_top';
    const TRANSOM_NUMERIC_RESP  = 'numeric_value';

    const isTransomDropChainEnd = (questionId: string, msgs: IAssistantMessage[]): boolean => {
        const msg = msgs.find(m => m.question?.questionId === questionId);
        if (!msg) return false;
        const optType = msg.question?.optionType;
        const hasResp = (msg.response?.answer?.length ?? 0) > 0 ||
                        !!(msg.response?.form && Object.keys(msg.response.form).length > 0);
        if (!hasResp) return false;

        // Custom Fraction form submitted → fire egress now
        if (optType === OPTION_TYPE_CUSTOM_FRACTION)
            return msgs.some(m => m.question?.optionType === OPTION_TYPE_TRANSOM_DROP);

        // Numeric Values form submitted → fire egress now
        if (optType === OPTION_TYPE_NUMERIC_VALUES)
            return msgs.some(m =>
                m.question?.optionType === OPTION_TYPE_TRANSOM_DROP &&
                (m.response?.answer?.[0]?.optionType === OPTION_TYPE_NUMERIC_VALUES ||
                 m.response?.answer?.[0]?.responseValue === TRANSOM_NUMERIC_RESP)
            );

        // Transom Drop question answered: only fire egress immediately for options
        // that don't require a follow-up form (Equal Glass, Equal Sash, etc.).
        // For Custom Fraction / Numeric Value, defer until the follow-up is submitted.
        if (optType === OPTION_TYPE_TRANSOM_DROP) {
            const selectedOpt = msg.response?.answer?.[0];
            // Check optionType of the selected option (reliable when config sets it correctly)
            if (selectedOpt?.optionType === OPTION_TYPE_CUSTOM_FRACTION) return false;
            if (selectedOpt?.optionType === OPTION_TYPE_NUMERIC_VALUES) return false;
            // Fallback: check responseValue strings
            const sel = selectedOpt?.responseValue;
            if (sel === TRANSOM_FRACTION_RESP || sel === TRANSOM_NUMERIC_RESP) return false;
            // Authoritative look-ahead: ask the question flow what the next question would be.
            // If it's a Custom Fraction or Numeric Values form, a follow-up is still needed.
            const nextQ = getNextVisibleQuestion(
                questionId,
                assistantConfig?.questionFlow ?? [],
                getAnswersFromMessages(msgs),
                getSelectedProductFromMessages(msgs),
                false,
                getFormValuesFromMessages(msgs),
            );
            if (nextQ?.optionType === OPTION_TYPE_CUSTOM_FRACTION) return false;
            if (nextQ?.optionType === OPTION_TYPE_NUMERIC_VALUES) return false;
            return true;
        }
        return false;
    };

    type FrameEgressDetail = {
        frameIndex: number;
        frameWidth: number;
        pass: 'sliding' | 'tilting' | 'both' | 'none' | 'not_applicable';
    };

    type EgressCheckResult =
        | { fails: true; frameDetails?: FrameEgressDetail[] }
        | { fails: false; pass: 'sliding' | 'tilting' | 'both'; multiFrame: boolean; frameDetails?: FrameEgressDetail[] }
        | null;

    const checkFireEgress = (msgs: IAssistantMessage[]): EgressCheckResult => {
        const dims = extractDimensionsFromMessages(msgs);
        const ptc = selectedProduct?.producttypecode;
        if (ptc === ProductStructureType.Arch) return null;
        const wType: FireEgressWindowType = ptc === ProductStructureType.Coupled ? 'couple'
            : ptc === ProductStructureType.Bays ? 'bay'
                : 'standard';
        if (!dims.windowHeight) return null;
        const { topSash, bottomSash } = resolveSashHeights('', dims.windowHeight, dims.topSash, dims.bottomSash);

        if (dims.frameWidths && dims.frameWidths.length > 1) {
            const multi = calculateFireEgressMultiFrame(dims.frameWidths, wType, dims.windowHeight, topSash, bottomSash, dims.frameSashOperations);
            const frameDetails: FrameEgressDetail[] = multi.frames.map(f => ({
                frameIndex: f.frameIndex,
                frameWidth: f.frameWidth,
                pass: !f.result.applicable ? 'not_applicable' : f.result.pass,
            }));
            if (!multi.anyCompliant) return { fails: true, frameDetails };
            const firstPass = multi.frames.find(f => f.result.applicable && f.result.pass !== 'none');
            const pass = (firstPass?.result as any)?.pass ?? 'sliding';
            return { fails: false, pass, multiFrame: true, frameDetails };
        }
        if (dims.windowWidth) {
            const result = calculateFireEgress({ windowType: wType, windowWidth: dims.windowWidth, windowHeight: dims.windowHeight, topSash, bottomSash, sashOperation: dims.sashOperation });
            if (!result.applicable) return null;
            if (result.pass === 'none') return { fails: true };
            return { fails: false, pass: result.pass, multiFrame: false };
        }
        return null;
    };

    const applyHardwareSkipAlert = (
        questionId: string,
        msgs: IAssistantMessage[],
    ): IAssistantMessage[] => {
        const qFlow = assistantConfig?.questionFlow ?? [];
        const currentQuestion = qFlow.find((q: any) => q.questionId === questionId);
        // Strip the old alert when a sash or hardware-required question is re-answered;
        // re-injection at the correct flow position is handled by proceedToNextQuestion.
        const stripsAlert = currentQuestion?.optionType === OPTION_TYPE_SASH
            || currentQuestion?.optionType === OPTION_TYPE_HARDWARE_REQUIRED;
        if (!stripsAlert) return msgs;
        return msgs.filter(m => !m.id?.startsWith('hardware_skip_alert_'));
    };

    const buildEgressAlertMessage = (egressResult: EgressCheckResult): IAssistantMessage | null => {
        if (!egressResult) return null;

        const id = `egress_alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const baseMsg = {
            id,
            question: {} as any,
            response: { answer: [], selectedOptionsIds: [], form: {} },
        };

        if (egressResult.fails) {
            return {
                ...baseMsg,
                type: 'alert' as const,
                alertVariant: 'error' as const,
                alertTitle: 'Fire Egress Not Compliant',
                message: 'This window does not meet fire egress regulations based on the dimensions entered. If you believe this is incorrect or need further guidance, please contact our internal sales team before proceeding. Do you still want it quoted?',
                alertActions: [
                    { label: 'Continue Anyway', actionId: 'egress_accept', variant: 'primary' as const },
                    { label: 'Cancel', actionId: 'egress_reject', variant: 'secondary' as const },
                ],
            };
        }

        const label = egressResult.pass === 'sliding' ? 'when sliding' : egressResult.pass === 'both' ? 'when sliding or tilting' : 'when bottom sash tilts';
        const suffix = egressResult.multiFrame ? ' (at least one frame compliant)' : '';
        return {
            ...baseMsg,
            type: 'alert' as const,
            alertVariant: 'success' as const,
            alertTitle: 'Fire Egress Compliant',
            message: `Window meets fire egress regulations ${label}${suffix}.`,
        };
    };

    const acceptEgressConsent = () => {
        setEgressConsentState('accepted');
        // Remove the egress alert card — user has consented, no need to show it
        const msgsWithoutAlert = messages.filter(m => !m.id!.startsWith('egress_alert_'));
        setMessages(msgsWithoutAlert);
        if (!egressConsentAfterQuestionId) return;
        const processingId = addProcessingMessage();
        proceedToNextQuestion(egressConsentAfterQuestionId, processingId, msgsWithoutAlert, selectedProduct);
    };

    const rejectEgressConsent = () => {
        setEgressConsentState('rejected');
        // Replace the alert with a "please adjust" message (no action buttons)
        setMessages(prev => prev.map(m =>
            m.id!.startsWith('egress_alert_')
                ? {
                    ...m,
                    alertVariant: 'warning' as const,
                    alertTitle: 'Adjustment Required',
                    message: 'Please adjust the window dimensions above to meet fire egress requirements.',
                    alertActions: undefined,
                    alertItems: undefined,
                }
                : m
        ));
    };

    // Single select handler
    const handleSingleSelect = (questionId: string, option: IQuestionOption) => {
        // Guard BEFORE mutating messages: without this, selecting "Add to Cart"
        // while a blend stage is still running marked the question as answered
        // (chat visually advanced) even though addToCart()'s own internal guard
        // below silently bailed out and never actually added the cart item.
        if (option.responseValue === 'addToCart' &&
            (isBlending || dbrBlend.isApplying || grillBlend.isApplying || colorizeBlend.isApplying)) {
            message.info('The window image is still being generated — please wait a moment before adding to cart.');
            return;
        }

        const botMessageIndex = messages.findIndex(
            msg => msg.type === 'assistant' && msg.question?.questionId === questionId
        );

        if (botMessageIndex === -1) return;

        const botMessage = messages[botMessageIndex];
        if (!botMessage) return;

        // Look up the fresh option from the bot message to get the latest priceList-resolved prices,
        // not the potentially stale object from the click event.
        const freshOption = botMessage.question?.options?.find((o) => o.optionId === option.optionId) ?? option;

        // Create user response message
        const userMessage = createUserResponseMessage(botMessage.question!, [freshOption]);

        // Build updated messages array
        const updatedMessages = [...messages];
        // Update bot message with user's response
        updatedMessages[botMessageIndex] = {
            ...updatedMessages[botMessageIndex],
            response: {
                answer: [freshOption],
                selectedOptionsIds: [freshOption.optionId],
                form: {}
            }
        };
        // Add user message
        updatedMessages.push(userMessage);

        // Check for pricing rules and add surge message if needed
        addPricingSurgeIfNeeded(botMessage.question!, [freshOption], updatedMessages);

        // Update state with new messages
        setMessages(updatedMessages);

        // Check if this is an "Add to Cart" action
        if (freshOption.responseValue === 'addToCart') {
            setSelectedProduct(undefined);
            // Add current configuration to cart
            addToCart();

        }

        // Check if this is a "View Cart" action
        if (option.responseValue === 'viewCart') {
            // Reset messages and navigate to quote summary tab (tab 3)
            setMessages([]);
            setActiveTab('3');
            return;
        }

        // Check if this is a "Proceed to Quote" action
        if (option.responseValue === 'proceedToQuote') {
            // Reset messages and navigate to quote summary tab (tab 3)
            setMessages([]);
            setActiveTab('3');
            return;
        }
        if (option.responseValue === 'configureAnother') {
            // Check if there are more window boxes to configure
            const propertyImage = quoteData?.customerInformation?.propertyImage;
            const allWindows = propertyImage?.windows || [];
            const configuredWindowIds = cart?.windowConfiguration
                .map(item => item.windowAnnotationId)
                .filter(Boolean) || [];
            const unconfiguredWindows = allWindows.filter(
                w => !configuredWindowIds.includes(w.id)
            );

            // Reset messages
            setMessages([]);

            // Navigate to customer info tab if:
            // 1. No property image selected, OR
            // 2. There are window boxes but all are configured
            if (!propertyImage?.imageUrl || (allWindows.length > 0 && unconfiguredWindows.length === 0)) {
                setActiveTab('1');
            }
            // Otherwise continue with window configuration flow (stay on tab 2)
            return;
        }

        // Determine the selected product to use
        let productToUse = selectedProduct;
        if ([OPTION_TYPE_VARIATION, OPTION_TYPE_WINDOW_TYPE].includes(option?.optionType || 0)) {
            const product = getProductFromOption(option);
            if (product) {
                productToUse = product;
                setSelectedProduct(product);
            }
        }
        // Fire egress: check when user selects Yes on the Egress Complaint question
        if (botMessage!.question?.optionType === OPTION_TYPE_EGRESS_COMPLAINT && option.responseValue === 'yes') {
            const egressResult = checkFireEgress(updatedMessages);
            const alertMsg = buildEgressAlertMessage(egressResult);
            if (alertMsg) {
                updatedMessages.push(alertMsg);
                setMessages([...updatedMessages]);
            }
            if (egressResult?.fails) {
                setEgressConsentState('pending');
                setEgressConsentAfterQuestionId(questionId);
                return;
            }
        }

        // Angled Cill Horn: add 2 bay poles when Yes, no change when No (first answer).
        // Only the user message carries cart/pricing data — do NOT patch the bot
        // message response to avoid duplicate "Dimension" entries in the breakdown.
        if (botMessage!.question?.optionType === OPTION_TYPE_ANGLED_CILL_HORN) {
            const isAngledSillHornsYes = option.responseValue === 'yes';
            if (isAngledSillHornsYes) {
                const reversedIdx = [...updatedMessages].reverse().findIndex(
                    m => m.type === 'user' && m.question?.optionType === DIMENSION_OPTION
                );
                if (reversedIdx !== -1) {
                    const realIdx = updatedMessages.length - 1 - reversedIdx;
                    const dimMsg = updatedMessages[realIdx];
                    const currentBayPoles = Number(dimMsg?.response?.form?.bay_poles) || BAY_POLES_DEFAULT;
                    const updatedForm = { ...dimMsg?.response?.form, bay_poles: currentBayPoles + 2 };
                    updatedMessages[realIdx] = {
                        ...dimMsg!,
                        response: { ...(dimMsg?.response ?? {}), form: updatedForm },
                    };
                }
            }

            const alertId = 'angled_cill_bay_poles_alert';
            const withoutOldAlert = updatedMessages.filter(m => m.id !== alertId);
            if (isAngledSillHornsYes) {
                withoutOldAlert.push({
                    id: alertId,
                    type: 'alert' as const,
                    alertVariant: 'info' as const,
                    alertTitle: 'Bay Poles Updated',
                    message: '2 additional bay poles have been added to your configuration.',
                    question: {} as any,
                    response: { answer: [], selectedOptionsIds: [], form: {} },
                });
            }
            updatedMessages.splice(0, updatedMessages.length, ...withoutOldAlert);
            setMessages([...updatedMessages]);
        }

        // Cill selection: inject an info alert so the user knows the cill colour
        // will match their profile colour and how to request a different one.
        // Strip any prior cill alert first so re-selecting doesn't duplicate it.
        if (botMessage.question?.optionType === OPTION_TYPE_CILL) {
            const filtered = updatedMessages.filter(m => !m.id?.startsWith('cill_colour_alert_'));
            if (/coloured/i.test(option.optionText ?? '')) {
                filtered.push({
                    id: `cill_colour_alert_${Date.now()}`,
                    type: 'alert' as const,
                    alertVariant: 'info' as const,
                    alertTitle: 'Cill Colour Information',
                    message:
                        'Please note that the cill colour will be supplied to match your selected profile colour. ' +
                        'If you require the cill in a different colour, please contact your administrator for further assistance.',
                    question: {} as any,
                    response: { answer: [], selectedOptionsIds: [], form: {} },
                });
            }
            updatedMessages.splice(0, updatedMessages.length, ...filtered);
            setMessages([...updatedMessages]);
        }

        // Hardware Required (Q0156): strip both the pre-question alert and any prior
        // hardware skip alert so re-answering always gives a clean state.
        // If "yes" is chosen the hardware skip alert must also be cleared — hardware
        // type will now be shown by the override in shouldShowQuestion.
        if (botMessage.question?.optionType === OPTION_TYPE_HARDWARE_REQUIRED) {
            const stripped = updatedMessages.filter(
                m => m.id !== 'hardware_required_alert' && !m.id?.startsWith('hardware_skip_alert_')
            );
            updatedMessages.splice(0, updatedMessages.length, ...stripped);
            setMessages([...updatedMessages]);
        }

        // Strip hardware skip alert if sash is being re-answered; re-injection at correct position in proceedToNextQuestion
        let strippedMsgs = applyHardwareSkipAlert(questionId, updatedMessages);
        if (strippedMsgs !== updatedMessages) {
            setMessages([...strippedMsgs]);
        }

        // Glazing type selected: update window type / variation stored prices from priceList
        if (botMessage.question?.optionType === OPTION_TYPE_GLAZING_TYPE) {
            const msgsWithoutGlazingAlert = strippedMsgs.filter(m => !m.id?.startsWith('glazing_price_alert_'));
            const findBaseMsg = (msgs: typeof strippedMsgs) =>
                msgs.find(m => m.type === 'user' && m.response?.answer?.[0]?.optionType === OPTION_TYPE_VARIATION) ??
                msgs.find(m => m.type === 'user' && m.response?.answer?.[0]?.optionType === OPTION_TYPE_WINDOW_TYPE);
            const baseMsg = findBaseMsg(msgsWithoutGlazingAlert);
            const oldPrice = baseMsg?.response?.answer?.[0]?.price;
            strippedMsgs = applyGlazingPriceToMessages(msgsWithoutGlazingAlert, assistantConfig?.questionFlow ?? []);
            const updatedBaseMsg = findBaseMsg(strippedMsgs);
            const newPrice = updatedBaseMsg?.response?.answer?.[0]?.price;
            const productLabel = updatedBaseMsg?.response?.answer?.[0]?.optionText;
            if (newPrice && newPrice !== oldPrice) {
                strippedMsgs = [...strippedMsgs, {
                    id: `glazing_price_alert_${Date.now()}`,
                    type: 'alert' as const,
                    alertVariant: 'info' as const,
                    alertTitle: 'Base Price Updated',
                    message: `The base price for ${productLabel ?? 'your window'} has been updated to ${formatPrice(newPrice)} based on your glazing selection.`,
                    question: {} as any,
                    response: { answer: [], selectedOptionsIds: [], form: {} },
                }];
            }
            setMessages([...strippedMsgs]);
        }

        // Add processing message and proceed to next question
        const processingId = addProcessingMessage();
        proceedToNextQuestion(questionId, processingId, strippedMsgs, productToUse);
    };

    // Multiple choice handler
    const handleMultipleChoice = (questionId: string, options: IQuestionOption[]) => {
        const botMessageIndex = messages.findIndex(
            msg => msg.type === 'assistant' && msg.question?.questionId === questionId
        );

        if (botMessageIndex === -1) return;

        const botMessage = messages[botMessageIndex];
        if (!botMessage) return;

        // Create user response message
        const userMessage = createUserResponseMessage(botMessage.question!, options);

        // Build updated messages array
        const updatedMessages = [...messages];
        // Update bot message with user's response
        updatedMessages[botMessageIndex] = {
            ...updatedMessages[botMessageIndex],
            response: {
                answer: options,
                selectedOptionsIds: options.map(opt => opt.optionId),
                form: {}
            }
        };
        // Add user message
        updatedMessages.push(userMessage);

        // Check for pricing rules and add surge message if needed
        addPricingSurgeIfNeeded(botMessage.question!, options, updatedMessages);

        // Update state with new messages
        setMessages(updatedMessages);

        // Add processing message and proceed to next question
        const processingId = addProcessingMessage();
        proceedToNextQuestion(questionId, processingId, updatedMessages, selectedProduct);
    };

    // Form submit handler
    const handleFormSubmit = (questionId: string, formValues: Obj) => {
        const botMessageIndex = messages.findIndex(
            msg => msg.type === 'assistant' && msg.question?.questionId === questionId
        );

        if (botMessageIndex === -1) return;

        const botMessage = messages[botMessageIndex];
        if (!botMessage) return;

        // Bay window dimensions: recalculate bay poles and coupling clips from angles.
        let bayWindowCouplingClips = -1; // -1 = not a bay dimension form
        let bayWindowBayPoles = 0;
        if (
            botMessage.question?.optionType === DIMENSION_OPTION &&
            selectedProduct?.producttypecode === ProductStructureType.Bays
        ) {
            const isAngledSillHornsYes = messages.some(
                m => m.type === 'user' &&
                     m.question?.optionType === OPTION_TYPE_ANGLED_CILL_HORN &&
                     m.response?.answer?.[0]?.responseValue === 'yes'
            );
            const { bayPoles, couplingClips, hasAngles } = calculateBayPolesAndCouplingClips(
                formValues as Record<string, unknown>,
                isAngledSillHornsYes,
            );
            if (hasAngles) {
                formValues = { ...formValues, bay_poles: bayPoles };
                bayWindowCouplingClips = couplingClips;
                bayWindowBayPoles = bayPoles;
            }
        }

        // Couple window dimensions: inject coupling clips based on frame count (frames - 1).
        let coupleWindowCouplingClips = -1;
        if (
            botMessage.question?.optionType === DIMENSION_OPTION &&
            selectedProduct?.producttypecode === ProductStructureType.Coupled
        ) {
            const frameCount = selectedProduct.numberOfFrames ?? 0;
            if (frameCount >= 2) {
                coupleWindowCouplingClips = frameCount - 1;
            }
        }

        // Convert form values to IQuestionOption format for consistency.
        // Skip metadata keys (e.g. __split_mode__) — those live in response.form
        // for UI-state restoration only and shouldn't appear in response.answer.
        const formAsOptions: IQuestionOption[] = Object.entries(formValues)
            .filter(([key]) => !key.startsWith('__'))
            .map(([key, value]) => {
                const option = botMessage!.question?.options?.find((opt: any) =>
                    opt.responseValue === key || opt.optionId === key
                );
                return option || {
                    optionId: key,
                    optionText: String(value),
                    responseValue: key,
                    description: '',
                    numberOfFrames: 0,
                    vssPrice: 0,
                    optionType: 0,
                    inputType: 0,
                    optionTypeName: '',
                    order: 0,
                    price: 0,
                    priceModifier: 0,
                    thumbnail: ''
                };
            });

        // Create user response message
        const userMessage = createUserResponseMessage(botMessage!.question!, formAsOptions, formValues);

        // Build updated messages array
        const updatedMessages = [...messages];
        // Update bot message with user's response
        updatedMessages[botMessageIndex] = {
            ...updatedMessages[botMessageIndex],
            response: {
                answer: formAsOptions,
                selectedOptionsIds: formAsOptions.map(opt => opt.optionId),
                form: formValues
            }
        };
        // Add user message
        updatedMessages.push(userMessage);

        // Check for pricing rules and add surge message if needed
        addPricingSurgeIfNeeded(botMessage!.question!, formValues, updatedMessages);

        // Update state with new messages
        setMessages(updatedMessages);

        // Bay window: strip any stale coupling clip message and inject a fresh one.
        if (bayWindowCouplingClips >= 0) {
            const stripped = updatedMessages.filter(m => m.id !== COUPLING_CLIP_MSG_ID);
            const clipMsg = buildCouplingClipMessage(bayWindowCouplingClips, assistantConfig?.questionFlow ?? []);
            if (clipMsg) stripped.push(clipMsg);
            updatedMessages.splice(0, updatedMessages.length, ...stripped);
            setMessages([...updatedMessages]);
        }

        // Couple window: strip any stale coupling clip message and inject a fresh one.
        if (coupleWindowCouplingClips >= 0) {
            const stripped = updatedMessages.filter(m => m.id !== COUPLING_CLIP_MSG_ID);
            const clipMsg = buildCouplingClipMessage(coupleWindowCouplingClips, assistantConfig?.questionFlow ?? []);
            if (clipMsg) stripped.push(clipMsg);
            updatedMessages.splice(0, updatedMessages.length, ...stripped);
            setMessages([...updatedMessages]);
        }

        // Dimension info alert: inform the user how many bay poles / coupling clips were added.
        if (bayWindowCouplingClips >= 0) {
            const poles = bayWindowBayPoles;
            const clips = bayWindowCouplingClips;
            const polesPart = `${poles} bay pole${poles !== 1 ? 's' : ''}`;
            const clipsPart = clips > 0 ? ` and ${clips} coupling clip pair${clips !== 1 ? 's' : ''}` : '';
            const withAlert = updatedMessages.filter(m => m.id !== DIMENSION_ALERT_ID);
            withAlert.push({
                id: DIMENSION_ALERT_ID,
                type: 'alert' as const,
                alertVariant: 'info' as const,
                alertTitle: 'Configuration Updated',
                message: `${polesPart}${clipsPart} have been calculated from your angles and added to your configuration.`,
                question: {} as any,
                response: { answer: [], selectedOptionsIds: [], form: {} },
            });
            updatedMessages.splice(0, updatedMessages.length, ...withAlert);
            setMessages([...updatedMessages]);
        } else if (coupleWindowCouplingClips >= 0) {
            const clips = coupleWindowCouplingClips;
            const withAlert = updatedMessages.filter(m => m.id !== DIMENSION_ALERT_ID);
            withAlert.push({
                id: DIMENSION_ALERT_ID,
                type: 'alert' as const,
                alertVariant: 'info' as const,
                alertTitle: 'Configuration Updated',
                message: `${clips} coupling clip pair${clips !== 1 ? 's' : ''} have been added to your coupled window configuration.`,
                question: {} as any,
                response: { answer: [], selectedOptionsIds: [], form: {} },
            });
            updatedMessages.splice(0, updatedMessages.length, ...withAlert);
            setMessages([...updatedMessages]);
        }

        // Spray colour alert: show when inside and outside spray colours differ.
        // Use the LAST matching user message for each colour type so that
        // re-answers (which push a new user message at the end) use the freshest
        // value rather than the first (stale) occurrence still in the message list.
        if (
            botMessage!.question?.optionType === OPTION_TYPE_INSIDE_SPRAY_COLOUR ||
            botMessage!.question?.optionType === OPTION_TYPE_OUTSIDE_SPRAY_COLOUR
        ) {
            const latestColourMsg = (optType: number) => {
                const all = updatedMessages.filter(m => m.type === 'user' && m.question?.optionType === optType);
                return all[all.length - 1] ?? null;
            };
            const insideUserMsg = latestColourMsg(OPTION_TYPE_INSIDE_SPRAY_COLOUR);
            const outsideUserMsg = latestColourMsg(OPTION_TYPE_OUTSIDE_SPRAY_COLOUR);
            const withoutSprayAlert = updatedMessages.filter(m => m.id !== SPRAY_COLOUR_ALERT_ID);
            if (insideUserMsg && outsideUserMsg) {
                const insideForm = insideUserMsg.response?.form ?? {};
                const outsideForm = outsideUserMsg.response?.form ?? {};
                const insideRal = String(insideForm['internal_ral_code'] ?? '').trim();
                const outsideRal = String(outsideForm['external_ral_code'] ?? '').trim();
                const insideFb = String(insideForm['internal_farrow_&_ball'] ?? '').trim();
                const outsideFb = String(outsideForm['external_farrow_&_ball'] ?? '').trim();
                const insideColour = insideRal || insideFb;
                const outsideColour = outsideRal || outsideFb;
                if (insideColour && outsideColour && insideColour.toLowerCase() !== outsideColour.toLowerCase()) {
                    withoutSprayAlert.push({
                        id: SPRAY_COLOUR_ALERT_ID,
                        type: 'alert' as const,
                        alertVariant: 'info' as const,
                        alertTitle: 'Two-Colour Spray — Price Doubled',
                        message: `The inside spray colour (${insideColour}) and outside spray colour (${outsideColour}) are different. Two separate spray runs are required, so the spray price has been doubled.`,
                        question: {} as any,
                        response: { answer: [], selectedOptionsIds: [], form: {} },
                    });
                }
            }
            updatedMessages.splice(0, updatedMessages.length, ...withoutSprayAlert);
            setMessages([...updatedMessages]);
        }

        const processingId = addProcessingMessage();
        proceedToNextQuestion(questionId, processingId, updatedMessages, selectedProduct);
    };

    // Text input handler
    const handleTextInput = (questionId: string, textValue: string) => {
        const botMessageIndex = messages.findIndex(
            msg => msg.type === 'assistant' && msg.question?.questionId === questionId
        );

        if (botMessageIndex === -1) return;

        const botMessage = messages[botMessageIndex];

        const textOption: IQuestionOption = {
            optionId: 'text_input',
            optionText: textValue,
            responseValue: textValue,
            description: '',
            vssPrice: 0,
            numberOfFrames: 0,
            optionType: 0,
            inputType: 1,
            optionTypeName: 'Text Input',
            order: 0,
            price: 0,
            priceModifier: 0,
            thumbnail: ''
        };

        // Create user response message
        const userMessage = createUserResponseMessage(botMessage!.question!, [textOption]);

        // Build updated messages array
        const updatedMessages = [...messages];
        // Update bot message with user's response
        updatedMessages[botMessageIndex] = {
            ...updatedMessages[botMessageIndex],
            response: {
                answer: [textOption],
                selectedOptionsIds: ['text_input'],
                form: { textValue }
            }
        };
        // Add user message
        updatedMessages.push(userMessage);

        // Check for pricing rules and add surge message if needed
        addPricingSurgeIfNeeded(botMessage!.question!, { textValue }, updatedMessages);

        // Update state with new messages
        setMessages(updatedMessages);

        const processingId = addProcessingMessage();
        proceedToNextQuestion(questionId, processingId, updatedMessages, selectedProduct);
    };

    // Resets window type / variation prices back to base config values in both
    // bot message question.options (displayed price cards) and user message
    // response.answer (used for price calculation).
    const resetWindowVariationPricesToBase = (msgs: IAssistantMessage[]): IAssistantMessage[] => {
        const flowOptionMap = new Map(
            (assistantConfig?.questionFlow ?? [])
                .flatMap(q => q.options ?? [])
                .map(o => [o.optionId, o])
        );
        const isWindowOrVariation = (optionType: number) =>
            optionType === OPTION_TYPE_WINDOW_TYPE || optionType === OPTION_TYPE_VARIATION;
        const resetOption = (a: IQuestionOption) => {
            if (!isWindowOrVariation(a.optionType)) return a;
            const flowOpt = flowOptionMap.get(a.optionId);
            if (!flowOpt) return a;
            return { ...a, price: flowOpt.price, priceModifier: flowOpt.priceModifier };
        };
        return msgs.map(m => {
            const hasWindowAnswer = m.response?.answer?.some(a => isWindowOrVariation(a.optionType));
            const hasWindowOption = m.question?.options?.some(o => isWindowOrVariation(o.optionType));
            if (!hasWindowAnswer && !hasWindowOption) return m;
            return {
                ...m,
                question: hasWindowOption
                    ? { ...m.question, options: m.question.options.map(resetOption) }
                    : m.question,
                response: hasWindowAnswer
                    ? { ...m.response, answer: m.response.answer.map(resetOption) }
                    : m.response,
            };
        });
    };

    // Update methods (for changing already submitted answers)
    const updateSingleSelect = (questionId: string, option: IQuestionOption) => {
        const botMessageIndex = messages.findIndex(
            msg => msg.type === 'assistant' && msg.question?.questionId === questionId
        );

        if (botMessageIndex === -1) return;

        const botMessage = messages[botMessageIndex];

        // Find user message index
        const userMessageIndex = messages.findIndex(
            msg => msg.type === 'user' && msg.question.questionId === questionId
        );

        if (userMessageIndex === -1) return;

        // Create updated user message
        const userMessage = createUserResponseMessage(botMessage!.question!, [option]);

        // Build updated messages array up to and including user message
        const updatedMessages = [...messages];
        // Update bot message with new response
        updatedMessages[botMessageIndex] = {
            ...updatedMessages[botMessageIndex],
            response: {
                answer: [option],
                selectedOptionsIds: [option.optionId],
                form: {}
            }
        };
        // Update user message
        updatedMessages[userMessageIndex] = {
            ...updatedMessages[userMessageIndex],
            message: userMessage.message,
            response: userMessage.response
        };

        // Slice to include only messages up to user message
        let messagesUpToUser = updatedMessages.slice(0, userMessageIndex + 1);

        // When window type, variation, or security is re-answered, reset all stored
        // window type / variation prices (both displayed option cards and stored answers)
        // back to base config values so the glazing alert comparison works correctly.
        if ([OPTION_TYPE_WINDOW_TYPE, OPTION_TYPE_VARIATION, OPTION_TYPE_SECURITY].includes(
            botMessage.question?.optionType ?? -1
        )) {
            messagesUpToUser = resetWindowVariationPricesToBase(messagesUpToUser);
        }

        // Check for pricing rules and add surge message if needed
        addPricingSurgeIfNeeded(botMessage!.question!, [option], messagesUpToUser);

        // Angled Cill Horn re-selection: +2 when changing No→Yes, -2 when Yes→No.
        if (botMessage!.question?.optionType === OPTION_TYPE_ANGLED_CILL_HORN) {
            const isAngledSillHornsYes = option.responseValue === 'yes';
            const prevCillAnswer = messages[userMessageIndex]?.response?.answer?.[0];
            const wasAngledSillHornsYes = prevCillAnswer?.responseValue === 'yes';
            if (isAngledSillHornsYes !== wasAngledSillHornsYes) {
                const reversedIdx = [...messagesUpToUser].reverse().findIndex(
                    m => m.type === 'user' && m.question?.optionType === DIMENSION_OPTION
                );
                if (reversedIdx !== -1) {
                    const realIdx = messagesUpToUser.length - 1 - reversedIdx;
                    const dimMsg = messagesUpToUser[realIdx];
                    const currentBayPoles = Number(dimMsg.response?.form?.bay_poles) || BAY_POLES_DEFAULT;
                    const newBayPoles = isAngledSillHornsYes
                        ? currentBayPoles + 2
                        : Math.max(BAY_POLES_DEFAULT, currentBayPoles - 2);
                    messagesUpToUser[realIdx] = {
                        ...dimMsg,
                        response: { ...dimMsg.response, form: { ...dimMsg.response?.form, bay_poles: newBayPoles } },
                    };
                }
            }

            const alertId = 'angled_cill_bay_poles_alert';
            const withoutOldAlert = messagesUpToUser.filter(m => m.id !== alertId);
            if (isAngledSillHornsYes) {
                withoutOldAlert.push({
                    id: alertId,
                    type: 'alert' as const,
                    alertVariant: 'info' as const,
                    alertTitle: 'Bay Poles Updated',
                    message: '2 additional bay poles have been added to your configuration.',
                    question: {} as any,
                    response: { answer: [], selectedOptionsIds: [], form: {} },
                });
            }
            messagesUpToUser.splice(0, messagesUpToUser.length, ...withoutOldAlert);
        }

        // Glazing type re-selected: update window type / variation stored prices from priceList
        if (botMessage.question?.optionType === OPTION_TYPE_GLAZING_TYPE) {
            const msgsWithoutGlazingAlert = messagesUpToUser.filter(m => !m.id?.startsWith('glazing_price_alert_'));
            const findBaseMsg = (msgs: typeof messagesUpToUser) =>
                msgs.find(m => m.type === 'user' && m.response?.answer?.[0]?.optionType === OPTION_TYPE_VARIATION) ??
                msgs.find(m => m.type === 'user' && m.response?.answer?.[0]?.optionType === OPTION_TYPE_WINDOW_TYPE);
            const baseMsg = findBaseMsg(msgsWithoutGlazingAlert);
            const oldPrice = baseMsg?.response?.answer?.[0]?.price;
            messagesUpToUser = applyGlazingPriceToMessages(msgsWithoutGlazingAlert, assistantConfig?.questionFlow ?? []);
            const updatedBaseMsg = findBaseMsg(messagesUpToUser);
            const newPrice = updatedBaseMsg?.response?.answer?.[0]?.price;
            const productLabel = updatedBaseMsg?.response?.answer?.[0]?.optionText;
            if (newPrice && newPrice !== oldPrice) {
                messagesUpToUser = [...messagesUpToUser, {
                    id: `glazing_price_alert_${Date.now()}`,
                    type: 'alert' as const,
                    alertVariant: 'info' as const,
                    alertTitle: 'Base Price Updated',
                    message: `The base price for ${productLabel ?? 'your window'} has been updated to ${formatPrice(newPrice)} based on your glazing selection.`,
                    question: {} as any,
                    response: { answer: [], selectedOptionsIds: [], form: {} },
                }];
            }
        }

        // Reset subsequent messages and proceed to next question
        resetAndProceedToNext(questionId, messagesUpToUser, option);
    };

    const updateMultipleChoice = (questionId: string, options: IQuestionOption[]) => {
        const botMessageIndex = messages.findIndex(
            msg => msg.type === 'assistant' && msg.question?.questionId === questionId
        );

        if (botMessageIndex === -1) return;

        const botMessage = messages[botMessageIndex];

        // Find user message index
        const userMessageIndex = messages.findIndex(
            msg => msg.type === 'user' && msg.question.questionId === questionId
        );

        if (userMessageIndex === -1) return;

        // Create updated user message
        const userMessage = createUserResponseMessage(botMessage!.question!, options);

        // Build updated messages array up to and including user message
        const updatedMessages = [...messages];
        // Update bot message with new response
        updatedMessages[botMessageIndex] = {
            ...updatedMessages[botMessageIndex],
            response: {
                answer: options,
                selectedOptionsIds: options.map(opt => opt.optionId),
                form: {}
            }
        };
        // Update user message
        updatedMessages[userMessageIndex] = {
            ...updatedMessages[userMessageIndex],
            message: userMessage.message,
            response: userMessage.response
        };

        // Slice to include only messages up to user message
        let messagesUpToUser = updatedMessages.slice(0, userMessageIndex + 1);

        // When window type, variation, or security is re-answered, reset all stored
        // window type / variation prices (both displayed option cards and stored answers)
        // back to base config values so the glazing alert comparison works correctly.
        if ([OPTION_TYPE_WINDOW_TYPE, OPTION_TYPE_VARIATION, OPTION_TYPE_SECURITY].includes(
            botMessage.question?.optionType ?? -1
        )) {
            messagesUpToUser = resetWindowVariationPricesToBase(messagesUpToUser);
        }

        // Check for pricing rules and add surge message if needed
        addPricingSurgeIfNeeded(botMessage!.question!, options, messagesUpToUser);

        // Reset subsequent messages and proceed to next question
        resetAndProceedToNext(questionId, messagesUpToUser);
    };

    const updateFormSubmit = (questionId: string, formValues: Obj) => {
        const botMessageIndex = messages.findIndex(
            msg => msg.type === 'assistant' && msg.question?.questionId === questionId
        );

        if (botMessageIndex === -1) return;

        const botMessage = messages[botMessageIndex];

        // Find user message index
        const userMessageIndex = messages.findIndex(
            msg => msg.type === 'user' && msg.question.questionId === questionId
        );

        if (userMessageIndex === -1) return;

        // Bay window dimensions: recalculate bay poles and coupling clips from angles.
        let bayWindowCouplingClips = -1;
        let bayWindowBayPoles = 0;
        if (
            botMessage.question?.optionType === DIMENSION_OPTION &&
            selectedProduct?.producttypecode === ProductStructureType.Bays
        ) {
            const isAngledSillHornsYes = messages.some(
                m => m.type === 'user' &&
                     m.question?.optionType === OPTION_TYPE_ANGLED_CILL_HORN &&
                     m.response?.answer?.[0]?.responseValue === 'yes'
            );
            const { bayPoles, couplingClips, hasAngles } = calculateBayPolesAndCouplingClips(
                formValues as Record<string, unknown>,
                isAngledSillHornsYes,
            );
            if (hasAngles) {
                formValues = { ...formValues, bay_poles: bayPoles };
                bayWindowCouplingClips = couplingClips;
                bayWindowBayPoles = bayPoles;
            }
        }

        // Couple window dimensions: inject coupling clips based on frame count (frames - 1).
        let coupleWindowCouplingClips = -1;
        if (
            botMessage.question?.optionType === DIMENSION_OPTION &&
            selectedProduct?.producttypecode === ProductStructureType.Coupled
        ) {
            const frameCount = selectedProduct.numberOfFrames ?? 0;
            if (frameCount >= 2) {
                coupleWindowCouplingClips = frameCount - 1;
            }
        }

        // Convert form values to IQuestionOption format. Skip metadata keys
        // (e.g. __split_mode__) — those live in response.form for UI-state
        // restoration only and shouldn't appear in response.answer.
        const formAsOptions: IQuestionOption[] = Object.entries(formValues)
            .filter(([key]) => !key.startsWith('__'))
            .map(([key, value]) => {
                const option = botMessage!.question?.options?.find((opt: any) =>
                    opt.responseValue === key || opt.optionId === key
                );
                return option || {
                    optionId: key,
                    optionText: String(value),
                    responseValue: key,
                    description: '',
                    numberOfFrames: 0,
                    optionType: 0,
                    inputType: 0,
                    optionTypeName: '',
                    order: 0,
                    price: 0,
                    vssPrice: 0,
                    priceModifier: 0,
                    thumbnail: ''
                };
            });

        // Create updated user message
        const userMessage = createUserResponseMessage(botMessage!.question!, formAsOptions, formValues);

        // Build updated messages array up to and including user message
        const updatedMessages = [...messages];
        // Update bot message with new response
        updatedMessages[botMessageIndex] = {
            ...updatedMessages[botMessageIndex],
            response: {
                answer: formAsOptions,
                selectedOptionsIds: formAsOptions.map(opt => opt.optionId),
                form: formValues
            }
        };
        // Update user message
        updatedMessages[userMessageIndex] = {
            ...updatedMessages[userMessageIndex],
            message: userMessage.message,
            response: userMessage.response
        };

        // Slice to include only messages up to user message
        let messagesUpToUser = updatedMessages.slice(0, userMessageIndex + 1);

        // Check for pricing rules and add surge message if needed
        addPricingSurgeIfNeeded(botMessage!.question!, formValues, messagesUpToUser);

        // Bay window: strip any stale coupling clip message and inject a fresh one.
        if (bayWindowCouplingClips >= 0) {
            const stripped = messagesUpToUser.filter(m => m.id !== COUPLING_CLIP_MSG_ID);
            const clipMsg = buildCouplingClipMessage(bayWindowCouplingClips, assistantConfig?.questionFlow ?? []);
            if (clipMsg) stripped.push(clipMsg);
            messagesUpToUser.splice(0, messagesUpToUser.length, ...stripped);
        }

        // Couple window: strip any stale coupling clip message and inject a fresh one.
        if (coupleWindowCouplingClips >= 0) {
            const stripped = messagesUpToUser.filter(m => m.id !== COUPLING_CLIP_MSG_ID);
            const clipMsg = buildCouplingClipMessage(coupleWindowCouplingClips, assistantConfig?.questionFlow ?? []);
            if (clipMsg) stripped.push(clipMsg);
            messagesUpToUser.splice(0, messagesUpToUser.length, ...stripped);
        }

        // Dimension info alert: inform the user how many bay poles / coupling clips were added.
        if (bayWindowCouplingClips >= 0) {
            const poles = bayWindowBayPoles;
            const clips = bayWindowCouplingClips;
            const polesPart = `${poles} bay pole${poles !== 1 ? 's' : ''}`;
            const clipsPart = clips > 0 ? ` and ${clips} coupling clip pair${clips !== 1 ? 's' : ''}` : '';
            const withAlert = messagesUpToUser.filter(m => m.id !== DIMENSION_ALERT_ID);
            withAlert.push({
                id: DIMENSION_ALERT_ID,
                type: 'alert' as const,
                alertVariant: 'info' as const,
                alertTitle: 'Configuration Updated',
                message: `${polesPart}${clipsPart} have been calculated from your angles and added to your configuration.`,
                question: {} as any,
                response: { answer: [], selectedOptionsIds: [], form: {} },
            });
            messagesUpToUser.splice(0, messagesUpToUser.length, ...withAlert);
        } else if (coupleWindowCouplingClips >= 0) {
            const clips = coupleWindowCouplingClips;
            const withAlert = messagesUpToUser.filter(m => m.id !== DIMENSION_ALERT_ID);
            withAlert.push({
                id: DIMENSION_ALERT_ID,
                type: 'alert' as const,
                alertVariant: 'info' as const,
                alertTitle: 'Configuration Updated',
                message: `${clips} coupling clip pair${clips !== 1 ? 's' : ''} have been added to your coupled window configuration.`,
                question: {} as any,
                response: { answer: [], selectedOptionsIds: [], form: {} },
            });
            messagesUpToUser.splice(0, messagesUpToUser.length, ...withAlert);
        }

        // Spray colour alert: show when inside and outside spray colours differ.
        if (
            botMessage!.question?.optionType === OPTION_TYPE_INSIDE_SPRAY_COLOUR ||
            botMessage!.question?.optionType === OPTION_TYPE_OUTSIDE_SPRAY_COLOUR
        ) {
            const latestColourMsg = (optType: number) => {
                const all = messagesUpToUser.filter(m => m.type === 'user' && m.question?.optionType === optType);
                return all[all.length - 1] ?? null;
            };
            const insideUserMsg = latestColourMsg(OPTION_TYPE_INSIDE_SPRAY_COLOUR);
            const outsideUserMsg = latestColourMsg(OPTION_TYPE_OUTSIDE_SPRAY_COLOUR);
            const withoutSprayAlert = messagesUpToUser.filter(m => m.id !== SPRAY_COLOUR_ALERT_ID);
            if (insideUserMsg && outsideUserMsg) {
                const insideForm = insideUserMsg.response?.form ?? {};
                const outsideForm = outsideUserMsg.response?.form ?? {};
                const insideRal = String(insideForm['internal_ral_code'] ?? '').trim();
                const outsideRal = String(outsideForm['external_ral_code'] ?? '').trim();
                const insideFb = String(insideForm['internal_farrow_&_ball'] ?? '').trim();
                const outsideFb = String(outsideForm['external_farrow_&_ball'] ?? '').trim();
                const insideColour = insideRal || insideFb;
                const outsideColour = outsideRal || outsideFb;
                if (insideColour && outsideColour && insideColour.toLowerCase() !== outsideColour.toLowerCase()) {
                    withoutSprayAlert.push({
                        id: SPRAY_COLOUR_ALERT_ID,
                        type: 'alert' as const,
                        alertVariant: 'info' as const,
                        alertTitle: 'Two-Colour Spray — Price Doubled',
                        message: `The inside spray colour (${insideColour}) and outside spray colour (${outsideColour}) are different. Two separate spray runs are required, so the spray price has been doubled.`,
                        question: {} as any,
                        response: { answer: [], selectedOptionsIds: [], form: {} },
                    });
                }
            }
            messagesUpToUser.splice(0, messagesUpToUser.length, ...withoutSprayAlert);
        }

        // Reset subsequent messages and proceed to next question
        resetAndProceedToNext(questionId, messagesUpToUser);
    };

    const updateTextInput = (questionId: string, textValue: string) => {
        const botMessageIndex = messages.findIndex(
            msg => msg.type === 'assistant' && msg.question?.questionId === questionId
        );

        if (botMessageIndex === -1) return;

        const botMessage = messages[botMessageIndex];

        // Find user message index
        const userMessageIndex = messages.findIndex(
            msg => msg.type === 'user' && msg.question.questionId === questionId
        );

        if (userMessageIndex === -1) return;

        const textOption: IQuestionOption = {
            optionId: 'text_input',
            optionText: textValue,
            responseValue: textValue,
            description: '',
            numberOfFrames: 0,
            optionType: 0,
            inputType: 1,
            vssPrice: 0,
            optionTypeName: 'Text Input',
            order: 0,
            price: 0,
            priceModifier: 0,
            thumbnail: ''
        };

        // Create updated user message
        const userMessage = createUserResponseMessage(botMessage!.question!, [textOption]);

        // Build updated messages array up to and including user message
        const updatedMessages = [...messages];
        // Update bot message with new response
        updatedMessages[botMessageIndex] = {
            ...updatedMessages[botMessageIndex],
            response: {
                answer: [textOption],
                selectedOptionsIds: ['text_input'],
                form: { textValue }
            }
        };
        // Update user message
        updatedMessages[userMessageIndex] = {
            ...updatedMessages[userMessageIndex],
            message: userMessage.message,
            response: userMessage.response
        };

        // Slice to include only messages up to user message
        let messagesUpToUser = updatedMessages.slice(0, userMessageIndex + 1);

        // Check for pricing rules and add surge message if needed
        addPricingSurgeIfNeeded(botMessage!.question!, { textValue }, messagesUpToUser);

        // Reset subsequent messages and proceed to next question
        resetAndProceedToNext(questionId, messagesUpToUser);
    };
    // Cart management methods
    const addToCart = () => {
        // Internal guard — not just a UI disabled-state. The button already
        // disables/shows loading on this combined flag (see `isBlending` in
        // the exposed state below), but addToCart is also reachable directly
        // from the chat flow (handleSingleSelect's `optionResponseValue ===
        // 'addToCart'` path), which isn't gated by any button at all. Without
        // this, a click/selection during DBR/colorize/grill/final-blend
        // processing silently captures a stale image (previous blend result,
        // or the raw template) instead of the one actually being generated.
        if (isBlending || dbrBlend.isApplying || grillBlend.isApplying || colorizeBlend.isApplying) {
            message.info('The window image is still being generated — please wait a moment before adding to cart.');
            return;
        }

        reset();
        grillBlend.reset();
        // Reset quantity to 1 after adding to cart
        setCurrentQuantity(1);
    };

    const removeFromCart = (itemId: string) => {
        if (!cart) return;

        const updatedCart: IAssistantCart = {
            ...cart,
            windowConfiguration: cart.windowConfiguration.filter(
                (item) => item.id !== itemId
            ),
        };

        // Save to storage if customer ID exists
        if (cart.customer?.customerId) {
            saveCustomerCart(cart.customer.customerId, updatedCart);
        
        }

        setCart(updatedCart);
    };

    const updateQuantity = (itemId: string, quantity: number) => {
        if (!cart || quantity < 1) return;

        const updatedCart: IAssistantCart = {
            ...cart,
            windowConfiguration: cart.windowConfiguration.map((item) =>
                item.id === itemId ? { ...item, quantity } : item
            ),
        };

        // Save to storage if customer ID exists
        if (cart.customer?.customerId) {
            saveCustomerCart(cart.customer.customerId, updatedCart);
            // Invalidate cart query to update header badge
            queryClient.invalidateQueries({ queryKey: ['cart-item-count'] });
        }

        setCart(updatedCart);
    };

    const updateWindowName = (itemId: string, newName: string) => {
        if (!cart || !newName || newName.trim().length === 0) return;

        const updatedCart: IAssistantCart = {
            ...cart,
            windowConfiguration: cart.windowConfiguration.map((item) =>
                item.id === itemId ? { ...item, windowName: newName.trim() } : item
            ),
        };

        // Save to storage if customer ID exists
        if (cart.customer?.customerId) {
            saveCustomerCart(cart.customer.customerId, updatedCart);
            // Invalidate cart query to update header badge
            queryClient.invalidateQueries({ queryKey: ['cart-item-count'] });
        }

        setCart(updatedCart);
    };

    const updateCartDiscount = (data: { discountPercentage?: number; discountAmount?: number; discountType: 'percentage' | 'amount' }) => {
        if (!cart) return;

        const updatedCart: IAssistantCart = {
            ...cart,
            customDiscountPercentage: data.discountPercentage || 0,
            customDiscountAmount: data.discountAmount || 0,
            discountType: data.discountType
        };

        // Save to storage if customer ID exists
        if (cart.customer?.customerId) {
            saveCustomerCart(cart.customer.customerId, updatedCart);
            // Invalidate cart query to update header badge
            queryClient.invalidateQueries({ queryKey: ['cart-item-count'] });
        }

        setCart(updatedCart);
    };

    const duplicateCartItem = (
        itemId: string,
        customName?: string,
        overrideImages?: {
            propertyImageUrl?: string;
            blendedImageUrl?: string;
            onlineBlendedImageUrl?: string;
            windowAnnotationId?: string;
            /** Full box coordinates of the selected window (x, y, width, height) */
            selectedWindowBox?: { x: number; y: number; width: number; height: number; label?: string };
            /** All blended window boxes — populated when multiple windows were blended at once */
            selectedWindowBoxes?: Array<{ id: string; label: string; x: number; y: number; width: number; height: number }>;
        },
    ) => {
        if (!cart) return;

        // Find the item to duplicate
        const itemToDuplicate = cart.windowConfiguration.find(item => item.id === itemId);
        if (!itemToDuplicate) return;

        // Strip "Specials Foil Surchage" from duplicated messages — it's a flat one-off
        // charge that applies to the original item only, not to copies.
        const duplicatedMessages = itemToDuplicate.messages.reduce<typeof itemToDuplicate.messages>((acc, msg) => {
            if (msg.type === 'price-surge' && msg.surgeItems?.some((s: any) => s.resolverValue === FOIL_SURCHARGE_RESOLVER)) {
                const filteredItems = msg.surgeItems.filter((s: any) => s.resolverValue !== FOIL_SURCHARGE_RESOLVER);
                if (filteredItems.length === 0) return acc;
                acc.push({ ...msg, surgeItems: filteredItems });
            } else {
                acc.push(msg);
            }
            return acc;
        }, []);
        const duplicatedPrice = calculatePriceFromMessages(duplicatedMessages, 'GBP');

        // Resolve the primary window box: prefer first entry from selectedWindowBoxes, fall back to selectedWindowBox
        const primaryBox = overrideImages?.selectedWindowBoxes?.[0] ?? overrideImages?.selectedWindowBox;
        const allBoxes = overrideImages?.selectedWindowBoxes;

        // Create a new cart item with duplicated data, optionally overriding images
        const duplicatedItem: IWindowCartItem = {
            ...itemToDuplicate,
            id: `cart-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            windowName: customName || `${itemToDuplicate.windowName} (Copy)`,
            quantity: overrideImages?.selectedWindowBoxes?.length ?? 1,
            timestamp: Date.now(),
            messages: duplicatedMessages,
            price: duplicatedPrice,
            // Clear blend/annotation data — duplicate starts fresh until user blends it
            blendedImageUrl: undefined,
            onlineBlendedImageUrl: undefined,
            windowAnnotationId: undefined,
            windows: undefined,
            ...(overrideImages && {
                propertyImageUrl: overrideImages.propertyImageUrl ?? itemToDuplicate.propertyImageUrl,
                blendedImageUrl: overrideImages.blendedImageUrl ?? undefined,
                onlineBlendedImageUrl: overrideImages.onlineBlendedImageUrl ?? undefined,
                ...(overrideImages.windowAnnotationId !== undefined && {
                    windowAnnotationId: overrideImages.windowAnnotationId,
                    // Use the selected window's actual box coordinates so editing the
                    // duplicate blends into the correct window, not the source window.
                    window: primaryBox
                        ? {
                            ...itemToDuplicate.window,
                            id: overrideImages.windowAnnotationId,
                            x: primaryBox.x,
                            y: primaryBox.y,
                            width: primaryBox.width,
                            height: primaryBox.height,
                            ...(primaryBox.label !== undefined && { label: primaryBox.label }),
                        }
                        : {
                            ...itemToDuplicate.window,
                            id: overrideImages.windowAnnotationId,
                        },
                    // When multiple windows were blended at once, store them all
                    ...(allBoxes && allBoxes.length > 1 && {
                        windows: allBoxes.map(b => ({
                            id: b.id,
                            label: b.label,
                            x: b.x,
                            y: b.y,
                            width: b.width,
                            height: b.height,
                        })),
                    }),
                }),
            }),
        };

        // Add to cart
        const updatedCart: IAssistantCart = {
            ...cart,
            windowConfiguration: [...cart.windowConfiguration, duplicatedItem],
        };

        // Save to storage if customer ID exists
        if (cart.customer?.customerId) {
            saveCustomerCart(cart.customer.customerId, updatedCart);
            // Invalidate cart query to update header badge
            queryClient.invalidateQueries({ queryKey: ['cart-item-count'] });
        }

        setCart(updatedCart);
    };

    const updateCartItem = (updatedItem: IWindowCartItem) => {
        if (!cart) return;

        const updatedCart: IAssistantCart = {
            ...cart,
            windowConfiguration: cart.windowConfiguration.map(item =>
                item.id === updatedItem.id ? updatedItem : item
            ),
        };

        // Save to storage if customer ID exists
        if (cart.customer?.customerId) {
            saveCustomerCart(cart.customer.customerId, updatedCart);
            // Invalidate cart query to update header badge
            queryClient.invalidateQueries({ queryKey: ['cart-item-count'] });
        }

        setCart(updatedCart);
    };

    // Replace the entire window configuration (used for delete + foil reconciliation).
    const replaceCartConfiguration = (items: IWindowCartItem[]) => {
        if (!cart) return;
        const updatedCart: IAssistantCart = { ...cart, windowConfiguration: items };
        if (cart.customer?.customerId) {
            saveCustomerCart(cart.customer.customerId, updatedCart);
            queryClient.invalidateQueries({ queryKey: ['cart-item-count'] });
        }
        setCart(updatedCart);
    };

    const clearCart = () => {
        if (!cart) return;

        // Clear from storage if customer ID exists
        if (cart.customer?.customerId) {
            clearCustomerCart(cart.customer.customerId);
            // Invalidate cart query to update header badge
            queryClient.invalidateQueries({ queryKey: ['cart-item-count'] });
        }

        // Reset cart state
        setActiveTab("1")
        setQuoteData(undefined)
        setCart(undefined);
    };

    // Quote flow methods
    const updateQuoteData = (updates: Partial<IQuoteFormData>) => {
        setQuoteData(prev => ({
            ...prev,
            ...updates,
        }));
    };

    const state: AssistantState = {
        messages,
        questionFlow: assistantConfig?.questionFlow ?? [],
        egressConsentState,
        cart: cart ?? { customer: {}, windowConfiguration: [] },
        cartItems: cart?.windowConfiguration ?? [],
        activeTab,
        selectedCustomerId,
        quoteData,
        assistantConfig: assistantConfig,
        // When no house image, fall back to template-processed image (DBR → Colorize → Grill → thumbnail)
        blendedImageUrl: blendedImageUrl ?? offlineImage
            ?? grillBlend.grillImageUrl ?? colorizeBlend.colorizedUrl ?? dbrBlend.dbrImageUrl ?? selectedProduct?.thumbnail ?? null,
        onLineBlendImageUrl,
        offlineImage,
        currentQuantity,
        // Combined with dbr/grill/colorize isApplying — mirrors
        // ConfigurationDrawerContext's isBlending. Raw isBlending alone only
        // reflects the FINAL AI blend step; it stays false while DBR/colorize/
        // grill are still processing (useBlendAiService isn't even enabled yet
        // at that point — see its `enabled` condition above), which let
        // Add-to-Cart fire mid-generation and capture a stale image.
        isBlending: isBlending || dbrBlend.isApplying || grillBlend.isApplying || colorizeBlend.isApplying,
        window,
        selectedProduct,
    };

    const method: AssistantMethods = {
        addMessage,
        addBotMessage,
        updateMessage,
        clearMessages,
        addProcessingMessage,
        removeProcessingMessage,
        handleSingleSelect,
        updateSingleSelect,
        handleMultipleChoice,
        updateMultipleChoice,
        handleFormSubmit,
        updateFormSubmit,
        handleTextInput,
        updateTextInput,
        addToCart,
        removeFromCart,
        updateQuantity,
        updateWindowName,
        updateCartDiscount,
        duplicateCartItem,
        updateCartItem,
        replaceCartConfiguration,
        clearCart,
        setActiveTab,
        setSelectedCustomerId,
        setQuoteData,
        updateQuoteData,
        setCurrentQuantity,
        setBlendMode: setCurrentBlendMode,
        acceptEgressConsent,
        rejectEgressConsent,
    };

    const value: AssistantContextType = {
        state,
        method,
    };

    return (
        <AssistantContext.Provider value={value}>
            {children}
        </AssistantContext.Provider>
    );
};

export const useAssistant = (): AssistantContextType => {
    const context = useContext(AssistantContext);
    if (!context) {
        throw new Error('useAssistant must be used within an AssistantProvider');
    }
    return context;
};
